import React from 'react';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Inventory2Outlined as LotIcon,
  LocalShipping as ReceiveIcon,
  Tune as AdjustIcon,
  SwapHoriz as TransferIcon,
  FactCheck as CountIcon,
  Warning as UncertainIcon,
} from '@mui/icons-material';

// ── Lot source type reference ──────────────────────────────────────────────────

const LOT_SOURCE_TYPES = [
  {
    label: 'Goods Receipt',
    color: '#1a7f37',
    bg: '#dafbe1',
    note: 'Created when stock arrives from a supplier via a purchase order. This is the primary inbound lot — provenance is fully known.',
    priority: 'First',
  },
  {
    label: 'Production',
    color: '#0969da',
    bg: '#dbeafe',
    note: 'Created by an in-house production or assembly workflow. Also shown first in lot selectors alongside Goods Receipt.',
    priority: 'First',
  },
  {
    label: 'Manual',
    color: '#6e7781',
    bg: '#f6f8fa',
    note: 'Created directly by a user with no source document. Used when you need to define a lot outside of a normal receiving workflow.',
    priority: '—',
  },
  {
    label: 'Adjustment',
    color: '#9a6700',
    bg: '#fef9c3',
    note: 'Auto-created when a positive manual adjustment is posted without specifying a lot. Provenance is uncertain — shown last in lot selectors.',
    priority: 'Last',
  },
  {
    label: 'Count Gain',
    color: '#cf222e',
    bg: '#ffebe9',
    note: 'Auto-created when a stock count finds more bottles than expected and no lot is specified for the gain. Shown last in selectors.',
    priority: 'Last',
  },
];

// ── Sub-components ─────────────────────────────────────────────────────────────

interface SectionProps {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon, title, children }) => {
  const theme = useTheme();
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <Box sx={{ color: theme.palette.primary.main, display: 'flex', fontSize: 18 }}>
          {icon}
        </Box>
        <Typography variant="subtitle1" fontWeight={700} sx={{ letterSpacing: '-0.01em' }}>
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
};

interface FieldRowProps {
  label: string;
  children: React.ReactNode;
  accent?: boolean;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, children, accent }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        py: 0.75,
        px: 1.25,
        borderRadius: 1.5,
        '&:hover': { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{
          minWidth: 140,
          flexShrink: 0,
          color: accent ? theme.palette.primary.main : theme.palette.text.primary,
        }}
      >
        {label}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
        {children}
      </Typography>
    </Box>
  );
};

interface NoteProps {
  children: React.ReactNode;
  variant?: 'info' | 'warning';
}

const Note: React.FC<NoteProps> = ({ children, variant = 'info' }) => {
  const theme = useTheme();
  const isWarning = variant === 'warning';
  return (
    <Box
      sx={{
        mt: 1.25,
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: isWarning
          ? (theme.palette.mode === 'dark' ? 'rgba(250,173,20,0.1)' : '#fffbeb')
          : (theme.palette.mode === 'dark' ? 'rgba(9,105,218,0.1)' : '#f0f7ff'),
        borderLeft: `3px solid ${isWarning ? '#d97706' : theme.palette.primary.main}`,
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {children}
      </Typography>
    </Box>
  );
};

// ── Main component ─────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
}

const LotsHelpModal: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: isDark ? 'rgba(0,0,0,0.55)' : 'rgba(30,30,50,0.35)',
          },
        },
      }}
      PaperProps={{
        sx: {
          background: isDark
            ? 'rgba(22, 22, 35, 0.90)'
            : 'rgba(255, 255, 255, 0.92)',
          backdropFilter: 'blur(32px)',
          WebkitBackdropFilter: 'blur(32px)',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
          boxShadow: isDark
            ? '0 32px 80px rgba(0,0,0,0.6)'
            : '0 32px 80px rgba(0,0,0,0.16)',
          borderRadius: 3,
          maxHeight: '88vh',
        },
      }}
    >
      {/* ── Sticky header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '12px 12px 0 0',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <LotIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2, letterSpacing: '-0.02em' }}>
              Lots & Batch Tracking Guide
            </Typography>
            <Typography variant="caption" color="text.secondary">
              How lot tracking works on this item
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: theme.palette.text.secondary,
            '&:hover': { color: theme.palette.text.primary, backgroundColor: theme.palette.action.hover },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* ── Scrollable body ─────────────────────────────────────────────────── */}
      <DialogContent sx={{ px: 3, py: 3, display: 'flex', flexDirection: 'column', gap: 3.5 }}>

        {/* Intro */}
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
          Lot tracking lets you follow individual batches of this wine — not just how many bottles you
          have, but <em>which delivery each bottle came from</em>. When lot tracking is enabled, every
          movement (receiving, adjustment, transfer, stock count) is linked to a specific lot so you
          can trace any bottle back to its source.
        </Typography>

        <Divider />

        {/* ── The Lots Table ──────────────────────────────────────────────────── */}
        <Section icon={<LotIcon fontSize="inherit" />} title="Reading the Lots Table">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Each row represents one batch. The columns tell you where it came from, how old it is, and
            how many bottles remain.
          </Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Lot Number" accent>
              The unique identifier for this batch. Auto-generated from the source document
              (e.g. a goods receipt number), or entered manually.
            </FieldRow>
            <FieldRow label="Received">
              The date this batch entered stock. Used to sort lots oldest-first (FIFO order) in all
              lot selectors across transfers, adjustments, and stock counts.
            </FieldRow>
            <FieldRow label="Expiry">
              Optional. If recorded, the system can use this for FEFO (first-expired, first-out)
              ordering. A dash means no expiry was recorded.
            </FieldRow>
            <FieldRow label="Unit Cost">
              The per-bottle cost at the time this lot was received. Used as a cost reference — the
              authoritative cost history is in the movement ledger below.
            </FieldRow>
            <FieldRow label="Status">
              <strong>Active</strong> — lot is live and will appear in lot selectors.{' '}
              <strong>Inactive</strong> — lot has been deactivated and is excluded from FIFO allocation.
            </FieldRow>
            <FieldRow label="Stock by Location">
              How many bottles from this specific lot are currently at each storage location. The total
              across all lots is the item's overall stock shown in the header above.
            </FieldRow>
          </Box>
          <Note>
            <strong>Supplier Batch Ref</strong> (shown below the lot number when present) is the supplier's
            own reference for that shipment — useful for tracing a batch back to a specific delivery note
            or invoice.
          </Note>
        </Section>

        <Divider />

        {/* ── Lot Source Types ────────────────────────────────────────────────── */}
        <Section icon={<ReceiveIcon fontSize="inherit" />} title="Lot Source Types">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.6 }}>
            Every lot has a source type that tells you how it was created. The type determines
            its position in lot selectors — Goods Receipt and Production lots appear first because
            their provenance is known.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {LOT_SOURCE_TYPES.map(st => (
              <Box
                key={st.label}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 2,
                  px: 1.25,
                  py: 1,
                  borderRadius: 1.5,
                  '&:hover': { backgroundColor: theme.palette.action.hover },
                }}
              >
                <Chip
                  label={st.label}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    color: st.color,
                    backgroundColor: st.bg,
                    border: 'none',
                    height: 22,
                    minWidth: 110,
                    flexShrink: 0,
                    mt: 0.1,
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                    {st.note}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  sx={{
                    flexShrink: 0,
                    mt: 0.2,
                    color: st.priority === 'First'
                      ? theme.palette.primary.main
                      : st.priority === 'Last'
                        ? theme.palette.text.disabled
                        : theme.palette.text.secondary,
                  }}
                >
                  {st.priority !== '—' ? `Shown ${st.priority.toLowerCase()}` : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        </Section>

        <Divider />

        {/* ── Unassigned stock ────────────────────────────────────────────────── */}
        <Section icon={<UncertainIcon fontSize="inherit" />} title="Unassigned Stock">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            If the blue info bar at the top of this section shows <em>"X units in unassigned stock"</em>,
            it means some bottles are recorded in the system without being linked to any lot.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            This typically happens when:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
            <FieldRow label="Lot tracking enabled late">
              Stock was received before lot tracking was turned on for this item. The existing
              balance was not retroactively assigned to a lot.
            </FieldRow>
            <FieldRow label="Pre-lot opening stock">
              An opening stock adjustment was posted without specifying a lot before the first
              goods receipt.
            </FieldRow>
          </Box>
          <Note variant="warning">
            Unassigned stock will be drawn down naturally as movements deplete the null-lot bucket.
            You can also clear it with a manual adjustment (stock out) and then re-receive against a
            proper lot if you need full traceability.
          </Note>
        </Section>

        <Divider />

        {/* ── How lots are created ────────────────────────────────────────────── */}
        <Section icon={<ReceiveIcon fontSize="inherit" />} title="How Lots Are Created">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            You do not normally need to create lots manually. They are generated automatically by
            the workflows that bring stock in.
          </Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Goods Receipt" accent>
              When a goods receipt is posted for this item, one Goods Receipt lot is created per
              line. It inherits the received date, supplier batch reference, and unit cost from the
              receipt.
            </FieldRow>
            <FieldRow label="Manual Adjustment (stock in)" accent>
              If you post a positive adjustment and select an existing lot, the bottles are added to
              that lot's bucket. If you leave the lot blank, the system auto-creates an Adjustment lot
              for those bottles.
            </FieldRow>
            <FieldRow label="Stock Count (gain)" accent>
              If a count finds more bottles than expected and no lot is specified, the system
              auto-creates a Count Gain lot to credit the variance.
            </FieldRow>
          </Box>
        </Section>

        <Divider />

        {/* ── Lots in other workflows ─────────────────────────────────────────── */}
        <Section icon={<TransferIcon fontSize="inherit" />} title="Lots Across Other Workflows">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            Once a lot exists, it follows the wine wherever it moves.
          </Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Transfers" accent>
              Each transfer line for this item requires a lot selection. The lot moves with the
              bottles — its balance decreases at the source location and increases at the destination.
              If you are moving bottles from two different lots, add two lines.
            </FieldRow>
            <FieldRow label="Manual Adjustment (stock out)" accent>
              You must select which lot's stock is being removed. The oldest Goods Receipt lot is
              shown first in the selector (FIFO order).
            </FieldRow>
            <FieldRow label="Stock Count" accent>
              Expected quantity shown on the count is the total across all lots. If the count finds
              a gain and no lot is specified, a Count Gain lot is created automatically.
            </FieldRow>
          </Box>
          <Note>
            <strong>Transfers require a lot.</strong> If you add a lot-tracked item to a transfer and
            try to post without selecting a lot on that line, the system will reject the posting. A lot
            is always required for lot-tracked items on a transfer.
          </Note>
        </Section>

        <Divider />

        {/* ── FIFO ordering ───────────────────────────────────────────────────── */}
        <Section icon={<CountIcon fontSize="inherit" />} title="FIFO Ordering">
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            In all lot selectors throughout the system, lots for this item are presented in a
            consistent order designed to help you work through stock in the right sequence.
          </Typography>
          <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${theme.palette.divider}` }}>
            <FieldRow label="Shown first">
              Goods Receipt and Production lots — the oldest received date is at the top.
              This is FIFO (first in, first out): the bottles that have been in stock the longest
              are offered first.
            </FieldRow>
            <FieldRow label="Shown last">
              Adjustment and Count Gain lots — labelled in the selector so you know their
              provenance is uncertain. Prefer Goods Receipt lots wherever possible.
            </FieldRow>
          </Box>
          <Note>
            The lot order in this table follows the same rule: oldest Goods Receipt / Production lots
            at the top, uncertain-provenance lots at the bottom.
          </Note>
        </Section>

        {/* ── Bottom spacer ──────────────────────────────────────────────────── */}
        <Box sx={{ height: 8 }} />

      </DialogContent>

      {/* ── Sticky footer ───────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'flex-end',
          background: isDark ? 'rgba(22,22,35,0.95)' : 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          borderRadius: '0 0 12px 12px',
        }}
      >
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ flex: 1, alignSelf: 'center', fontSize: '0.72rem' }}
        >
          Full documentation is available in the lots & batch tracking manual.
        </Typography>
        <Box
          component="button"
          onClick={onClose}
          sx={{
            px: 3,
            py: 1,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'transparent',
            color: theme.palette.text.primary,
            fontWeight: 600,
            fontSize: '0.875rem',
            cursor: 'pointer',
            fontFamily: 'inherit',
            transition: 'all 0.15s',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
              borderColor: theme.palette.text.secondary,
            },
          }}
        >
          Close
        </Box>
      </Box>
    </Dialog>
  );
};

export default LotsHelpModal;
